<template>
  <div class="doc-section">
    <div class="section-header">
      <span class="material-icons-sharp section-icon">construction</span>
      <h2>Paramétrage</h2>
    </div>

    <p class="section-intro">
      Le module Paramétrage permet de configurer les données de référence du système :
      produits, fournisseurs, transitaires, magasins, utilisateurs et types de conteneurs.
      Le module est organisé en <strong>6 onglets</strong>. <strong>Accès :</strong> Administrateur uniquement.
    </p>

    <!-- ===== ONGLET PRODUIT ===== -->
    <h3 class="sub-title">Onglet 1 : Produits</h3>
    <p class="section-text">Cet onglet gère le catalogue des produits commercialisés.</p>

    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Champ</th><th>Type</th><th>Obligatoire</th><th>Ce qu'il faut saisir</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Nom</strong></td><td>Texte</td><td><span class="required-badge badge-yes">Oui</span></td><td>Le nom commercial du produit. Exemple : « Riz Thaï 25kg »</td></tr>
          <tr><td><strong>Type</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-yes">Oui</span></td><td>Sélectionnez la catégorie : Carton, Boîte, Sac, Bidon, Palette, etc.</td></tr>
          <tr><td><strong>Unité de mesure</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-yes">Oui</span></td><td>L'unité du produit : Kg, Litre, Pièce, Tonne, etc.</td></tr>
          <tr><td><strong>Prix Unitaire</strong></td><td>Nombre</td><td><span class="required-badge badge-cond">Conditionnel</span></td><td>Le prix de vente unitaire en FCFA. Visible uniquement pour les rôles Admin et Resp. financier.</td></tr>
          <tr><td><strong>Observation</strong></td><td>Texte</td><td><span class="required-badge badge-no">Non</span></td><td>Notes supplémentaires : fournisseur habituel, conditionnement spécial, etc.</td></tr>
        </tbody>
      </table>
    </div>

    <h4 class="sub-title">Comment enregistrer un nouveau produit</h4>
    <div class="steps">
      <div class="step-item">
        <div class="step-num">1</div>
        <p><strong>Ouvrir le Paramétrage</strong> — Dans le menu latéral gauche, cliquez sur l'icône engrenage <strong>Paramétrage</strong>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">2</div>
        <p><strong>Rester sur l'onglet « Produits »</strong> — C'est l'onglet affiché par défaut quand vous ouvrez Paramétrage.</p>
      </div>
      <div class="step-item">
        <div class="step-num">3</div>
        <p><strong>Cliquer sur le bouton <span class="btn-ref">Ajouter</span></strong> — Un formulaire modal s'ouvre avec le titre « Produit ».</p>
      </div>
      <div class="step-item">
        <div class="step-num">4</div>
        <p><strong>Remplir le champ « Nom »</strong> — Saisissez le nom du produit (ex : « Lait Gloria 400g »).</p>
      </div>
      <div class="step-item">
        <div class="step-num">5</div>
        <p><strong>Sélectionner le « Type »</strong> — Cliquez sur le champ <em>Type</em>, la liste déroulante s'ouvre. Choisissez la catégorie (ex : Carton).</p>
      </div>
      <div class="step-item">
        <div class="step-num">6</div>
        <p><strong>Sélectionner l'« Unité de mesure »</strong> — Cliquez sur le champ, puis choisissez l'unité (ex : Kg).</p>
      </div>
      <div class="step-item">
        <div class="step-num">7</div>
        <p><strong>Saisir le « Prix Unitaire »</strong> — Tapez le prix de vente en FCFA (ex : 15000). Ce champ n'apparaît que pour Admin et Resp. financier.</p>
      </div>
      <div class="step-item">
        <div class="step-num">8</div>
        <p><strong>Ajouter une observation (facultatif)</strong> — Saisissez des notes si nécessaire.</p>
      </div>
      <div class="step-item">
        <div class="step-num">9</div>
        <p><strong>Cliquer sur <span class="btn-ref">Soumettre</span></strong> — Le produit est enregistré. Une notification verte confirme le succès. Le tableau se met à jour automatiquement.</p>
      </div>
    </div>

    <!-- ===== ONGLET FOURNISSEUR ===== -->
    <h3 class="sub-title">Onglet 2 : Fournisseurs</h3>
    <p class="section-text">Gestion des fournisseurs auprès desquels la société s'approvisionne.</p>

    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Champ</th><th>Type</th><th>Obligatoire</th><th>Ce qu'il faut saisir</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Nom</strong></td><td>Texte</td><td><span class="required-badge badge-yes">Oui</span></td><td>Raison sociale du fournisseur. Exemple : « Thai Rice Export Co. »</td></tr>
          <tr><td><strong>Téléphone</strong></td><td>Texte</td><td><span class="required-badge badge-yes">Oui</span></td><td>Numéro de téléphone principal, avec indicatif international.</td></tr>
          <tr><td><strong>Mail</strong></td><td>Email</td><td><span class="required-badge badge-yes">Oui</span></td><td>Adresse email du fournisseur.</td></tr>
          <tr><td><strong>Pays</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-yes">Oui</span></td><td>Pays d'origine du fournisseur. Sélectionnez dans la liste des pays.</td></tr>
          <tr><td><strong>Correspondant</strong></td><td>Texte</td><td><span class="required-badge badge-no">Non</span></td><td>Nom de la personne de contact chez le fournisseur.</td></tr>
          <tr><td><strong>Téléphone correspondant</strong></td><td>Texte</td><td><span class="required-badge badge-no">Non</span></td><td>Numéro du correspondant.</td></tr>
          <tr><td><strong>Observation</strong></td><td>Texte</td><td><span class="required-badge badge-no">Non</span></td><td>Notes complémentaires.</td></tr>
        </tbody>
      </table>
    </div>

    <h4 class="sub-title">Comment enregistrer un fournisseur</h4>
    <div class="steps">
      <div class="step-item">
        <div class="step-num">1</div>
        <p>Cliquez sur l'onglet <strong>« Fournisseurs »</strong> en haut de la page Paramétrage.</p>
      </div>
      <div class="step-item">
        <div class="step-num">2</div>
        <p>Cliquez sur <span class="btn-ref">Ajouter</span>. Le formulaire « Fournisseur » s'ouvre.</p>
      </div>
      <div class="step-item">
        <div class="step-num">3</div>
        <p>Remplissez les champs : <em>Nom</em>, <em>Téléphone</em>, <em>Mail</em>, sélectionnez le <em>Pays</em> dans la liste. Ajoutez le nom et téléphone du correspondant si vous les avez.</p>
      </div>
      <div class="step-item">
        <div class="step-num">4</div>
        <p>Cliquez sur <span class="btn-ref">Soumettre</span>. Le fournisseur est enregistré.</p>
      </div>
    </div>

    <!-- ===== ONGLET TRANSITAIRE ===== -->
    <h3 class="sub-title">Onglet 3 : Transitaires</h3>
    <p class="section-text">Les transitaires gèrent le transit des marchandises (dédouanement, transport).</p>

    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Champ</th><th>Type</th><th>Obligatoire</th><th>Ce qu'il faut saisir</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Nom</strong></td><td>Texte</td><td><span class="required-badge badge-yes">Oui</span></td><td>Nom ou raison sociale du transitaire.</td></tr>
          <tr><td><strong>Téléphone</strong></td><td>Texte</td><td><span class="required-badge badge-yes">Oui</span></td><td>Numéro de téléphone.</td></tr>
          <tr><td><strong>Mail</strong></td><td>Email</td><td><span class="required-badge badge-yes">Oui</span></td><td>Adresse email.</td></tr>
          <tr><td><strong>Observation</strong></td><td>Texte</td><td><span class="required-badge badge-no">Non</span></td><td>Notes complémentaires.</td></tr>
        </tbody>
      </table>
    </div>

    <p class="section-text">
      <strong>Procédure :</strong> Cliquez sur l'onglet <strong>« Transitaires »</strong> → <span class="btn-ref">Ajouter</span> → Remplissez <em>Nom</em>, <em>Téléphone</em>, <em>Mail</em> → <span class="btn-ref">Soumettre</span>.
    </p>

    <!-- ===== ONGLET MAGASIN ===== -->
    <h3 class="sub-title">Onglet 4 : Magasins</h3>
    <p class="section-text">Configuration des entrepôts/magasins de stockage.</p>

    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Champ</th><th>Type</th><th>Obligatoire</th><th>Ce qu'il faut saisir</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Nom</strong></td><td>Texte</td><td><span class="required-badge badge-yes">Oui</span></td><td>Nom du magasin. Exemple : « Magasin Central Abidjan »</td></tr>
          <tr><td><strong>Adresse</strong></td><td>Texte</td><td><span class="required-badge badge-yes">Oui</span></td><td>Adresse physique du magasin.</td></tr>
          <tr><td><strong>Téléphone</strong></td><td>Texte</td><td><span class="required-badge badge-yes">Oui</span></td><td>Numéro de téléphone du magasin.</td></tr>
          <tr><td><strong>Responsable</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-yes">Oui</span></td><td>Sélectionnez l'utilisateur responsable parmi la liste des utilisateurs existants.</td></tr>
          <tr><td><strong>Longitude</strong></td><td>Nombre</td><td><span class="required-badge badge-no">Non</span></td><td>Coordonnée GPS longitude (ex: -3.99). Utilisée pour la géolocalisation.</td></tr>
          <tr><td><strong>Latitude</strong></td><td>Nombre</td><td><span class="required-badge badge-no">Non</span></td><td>Coordonnée GPS latitude (ex: 5.32). Utilisée pour la géolocalisation.</td></tr>
        </tbody>
      </table>
    </div>

    <h4 class="sub-title">Comment créer un magasin</h4>
    <div class="steps">
      <div class="step-item">
        <div class="step-num">1</div>
        <p>Cliquez sur l'onglet <strong>« Magasins »</strong>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">2</div>
        <p>Cliquez sur <span class="btn-ref">Ajouter</span>. Le formulaire « Magasin » s'ouvre.</p>
      </div>
      <div class="step-item">
        <div class="step-num">3</div>
        <p>Remplissez <em>Nom</em>, <em>Adresse</em>, <em>Téléphone</em>. Sélectionnez le <em>Responsable</em> dans la liste. Optionnellement, saisissez les coordonnées GPS.</p>
      </div>
      <div class="step-item">
        <div class="step-num">4</div>
        <p>Si vous avez saisi les coordonnées GPS, cliquez sur <span class="btn-ref">Vérifier la localisation</span> pour visualiser l'emplacement sur la carte.</p>
      </div>
      <div class="step-item">
        <div class="step-num">5</div>
        <p>Cliquez sur <span class="btn-ref">Soumettre</span>. Le magasin est créé.</p>
      </div>
    </div>

    <!-- ===== ONGLET UTILISATEUR ===== -->
    <h3 class="sub-title">Onglet 5 : Utilisateurs</h3>
    <p class="section-text">Création et gestion des comptes utilisateurs du système.</p>

    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Champ</th><th>Type</th><th>Obligatoire</th><th>Ce qu'il faut saisir</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Type d'utilisateur</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-yes">Oui</span></td><td>Le rôle de l'utilisateur : Administrateur, Resp. financier, Resp. opérations, Caisse, Magasinier, Contrôleur ou Accueil.</td></tr>
          <tr><td><strong>Magasin</strong></td><td>Liste déroulante (multiple)</td><td><span class="required-badge badge-cond">Conditionnel</span></td><td>Le(s) magasin(s) assigné(s). Ce champ n'apparaît que pour les rôles Magasinier, Contrôleur et Resp. opérations. Les autres rôles n'ont pas de magasin attitré.</td></tr>
          <tr><td><strong>Nom Complet</strong></td><td>Texte</td><td><span class="required-badge badge-yes">Oui</span></td><td>Prénom et nom de l'utilisateur.</td></tr>
          <tr><td><strong>Numéro de téléphone</strong></td><td>Texte</td><td><span class="required-badge badge-yes">Oui</span></td><td>Numéro de téléphone professionnel.</td></tr>
          <tr><td><strong>Email</strong></td><td>Email</td><td><span class="required-badge badge-yes">Oui</span></td><td>Adresse email qui servira d'identifiant de connexion. Doit être unique.</td></tr>
          <tr><td><strong>Mot de passe</strong></td><td>Texte</td><td><span class="required-badge badge-cond">Conditionnel</span></td><td>Visible uniquement en mode modification. Permet de réinitialiser le mot de passe.</td></tr>
        </tbody>
      </table>
    </div>

    <h4 class="sub-title">Comment créer un utilisateur</h4>
    <div class="steps">
      <div class="step-item">
        <div class="step-num">1</div>
        <p>Cliquez sur l'onglet <strong>« Utilisateurs »</strong>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">2</div>
        <p>Cliquez sur <span class="btn-ref">Ajouter</span>. Le formulaire « Utilisateur » s'ouvre.</p>
      </div>
      <div class="step-item">
        <div class="step-num">3</div>
        <p>Sélectionnez le <em>Type d'utilisateur</em> (rôle) dans la liste déroulante. Si le rôle est Magasinier ou Contrôleur, le champ <em>Magasin</em> apparaît : sélectionnez le(s) magasin(s).</p>
      </div>
      <div class="step-item">
        <div class="step-num">4</div>
        <p>Remplissez <em>Nom Complet</em>, <em>Numéro de téléphone</em>, <em>Email</em>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">5</div>
        <p>Cliquez sur <span class="btn-ref">Soumettre</span>. Un mot de passe par défaut est généré. L'utilisateur devra le changer à sa première connexion.</p>
      </div>
    </div>

    <!-- ===== ONGLET CONTENEUR ===== -->
    <h3 class="sub-title">Onglet 6 : Types de conteneurs</h3>
    <p class="section-text">Définition des types de conteneurs utilisés pour le transport maritime.</p>

    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Champ</th><th>Type</th><th>Obligatoire</th><th>Ce qu'il faut saisir</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Type de conteneur</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-yes">Oui</span></td><td>Sélectionnez un type parmi la liste (20 pieds, 40 pieds, etc.).</td></tr>
          <tr><td><strong>Description</strong></td><td>Texte</td><td><span class="required-badge badge-no">Non</span></td><td>Description supplémentaire du type de conteneur.</td></tr>
        </tbody>
      </table>
    </div>

    <p class="section-text">
      <strong>Procédure :</strong> Onglet <strong>« Conteneurs »</strong> → <span class="btn-ref">Ajouter</span> → Sélectionnez le <em>Type de conteneur</em>, ajoutez une <em>Description</em> → <span class="btn-ref">Soumettre</span>.
    </p>

    <!-- ===== MODIFIER / SUPPRIMER ===== -->
    <h3 class="sub-title">Modifier ou supprimer un élément</h3>
    <div class="steps">
      <div class="step-item">
        <div class="step-num">1</div>
        <p><strong>Modifier :</strong> Dans le tableau, cliquez sur l'icône crayon (✏️) sur la ligne à modifier. Le formulaire s'ouvre avec les données pré-remplies. Modifiez les champs puis cliquez sur <span class="btn-ref">Soumettre</span>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">2</div>
        <p><strong>Supprimer :</strong> Cliquez sur l'icône corbeille (🗑️) sur la ligne. Une boîte de confirmation affiche « Êtes-vous sûr de vouloir supprimer cet élément du système ? ». Cliquez sur <span class="btn-ref">Oui je veux</span> pour confirmer.</p>
      </div>
    </div>

    <div class="info-card warning">
      <div class="card-title">
        <span class="material-icons-sharp" style="font-size:16px;color:#ba4f4f">warning</span>
        Attention
      </div>
      <p>
        Un produit, fournisseur ou magasin utilisé dans des commandes ou des BL ne peut pas être supprimé. Le système bloque la suppression pour préserver l'intégrité des données.
      </p>
    </div>
  </div>
</template>
<script>
export default { name: "DocParametrage" };
</script>
